import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { companiesService, GroupOverviewCompany } from '../services/companiesService'

const maturityLabels: Record<string, string> = {
  startup: 'Startup',
  traction: 'Tração',
  growth: 'Growth',
  scale: 'Scale',
  mature: 'Madura',
}

const maturityBadge: Record<string, string> = {
  startup: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  traction: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  growth: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  scale: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  mature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
}

function fmt(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return 'R$ 0'
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

function progressColor(pct: number): string {
  if (pct >= 100) return 'bg-green-500'
  if (pct >= 80) return 'bg-yellow-500'
  return 'bg-red-500'
}

function getQuarter(month: number) {
  return Math.ceil(month / 3) as 1 | 2 | 3 | 4
}

interface Props {
  primaryCompanyId?: number
}

export const GroupHealthOverview = ({ primaryCompanyId }: Props) => {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>(getQuarter(now.getMonth() + 1))

  const { data, isLoading } = useQuery({
    queryKey: ['group-overview', year, quarter],
    queryFn: () => companiesService.getGroupOverview(year, quarter),
    staleTime: 60_000,
  })

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    )
  }

  if (!data || data.companies.length === 0) return null

  // Sort: primary first, then by name
  const sorted = [...data.companies].sort((a, b) => {
    if (a.company.id === primaryCompanyId) return -1
    if (b.company.id === primaryCompanyId) return 1
    return a.company.name.localeCompare(b.company.name)
  })

  return (
    <div className="bg-white dark:bg-gray-800 rounded-card shadow mb-6">
      {/* Header + Quarter Selector */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Empresas do Grupo — Q{quarter}/{year}
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex rounded-btn border border-gray-300 dark:border-gray-600 overflow-hidden">
              {([1, 2, 3, 4] as const).map((q) => (
                <button
                  key={q}
                  onClick={() => setQuarter(q)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    quarter === q
                      ? 'bg-primary text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  Q{q}
                </button>
              ))}
            </div>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Company Cards Grid */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map((item) => (
          <CompanyCard
            key={item.company.id}
            item={item}
            isPrimary={item.company.id === primaryCompanyId}
          />
        ))}
      </div>
    </div>
  )
}

function CompanyCard({
  item,
  isPrimary,
}: {
  item: GroupOverviewCompany
  isPrimary: boolean
}) {
  const { company, thresholds, quarterly_result: qr } = item
  const mrrMin = parseFloat(thresholds.mrr_min)
  const marginMin = parseFloat(thresholds.margin_min)
  const noMrrMin = mrrMin === 0

  const avgMrr = qr ? parseFloat(qr.average_mrr) : 0
  const avgMargin = qr ? parseFloat(qr.average_margin) : 0
  const monthsReported = qr?.months_reported ?? 0

  const mrrPct = mrrMin > 0 ? (avgMrr / mrrMin) * 100 : (avgMrr > 0 ? 100 : 0)
  const marginPct = marginMin > 0 ? (avgMargin / marginMin) * 100 : 100

  const status = qr
    ? (qr.is_eligible ? 'eligible' : 'suspended')
    : 'waiting'

  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate(`/empresas/${company.id}/dashboard`)}
      className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${
        isPrimary
          ? 'border-primary/50 bg-primary/5 dark:bg-primary/10 hover:border-primary'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 hover:border-gray-400 dark:hover:border-gray-500'
      }`}
    >
      {/* Company Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {isPrimary && (
            <span className="flex-shrink-0 text-xs text-primary font-medium">Sua empresa</span>
          )}
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{company.name}</h3>
          <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${maturityBadge[company.maturity_stage]}`}>
            {maturityLabels[company.maturity_stage]}
          </span>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Indicators */}
      <div className="space-y-3">
        {/* MRR */}
        <div>
          <div className="flex justify-between items-baseline text-sm">
            <span className="text-gray-500 dark:text-gray-400">MRR</span>
            <span className="font-semibold text-gray-900 dark:text-white">{fmt(avgMrr)}</span>
          </div>
          {!noMrrMin && (
            <>
              <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${progressColor(mrrPct)}`}
                  style={{ width: `${Math.min(mrrPct, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {mrrPct.toFixed(0)}% do mínimo ({fmt(mrrMin)})
              </p>
            </>
          )}
          {noMrrMin && (
            <p className="text-[10px] text-gray-400 mt-0.5">Sem mínimo exigido</p>
          )}
        </div>

        {/* Margin */}
        <div>
          <div className="flex justify-between items-baseline text-sm">
            <span className="text-gray-500 dark:text-gray-400">Margem</span>
            <span className={`font-semibold ${
              avgMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {avgMargin.toFixed(1)}%
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${progressColor(marginPct)}`}
              style={{ width: `${Math.min(marginPct, 100)}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {`${marginPct.toFixed(0)}% do mínimo (${marginMin}%)`}
          </p>
        </div>

        {/* Months */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Meses</span>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`h-2 w-5 rounded-sm ${
                    i < monthsReported ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{monthsReported}/3</span>
          </div>
        </div>
      </div>

      {/* Suspension reason */}
      {status === 'suspended' && qr?.suspension_reason && (
        <p className="mt-2 text-[11px] text-red-500 dark:text-red-400 line-clamp-2">
          {qr.suspension_reason}
        </p>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: 'eligible' | 'suspended' | 'waiting' }) {
  if (status === 'eligible') {
    return (
      <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
        Elegível
      </span>
    )
  }
  if (status === 'suspended') {
    return (
      <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
        Inativa
      </span>
    )
  }
  return (
    <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
      Aguardando
    </span>
  )
}

export default GroupHealthOverview
